import { Controller } from "@hotwired/stimulus"

// The three-step accordion behind season setup.
//
// Steps collapse, they never disappear: a finished step becomes a one-line
// summary you can click to reopen in place. There is no back button and
// nothing navigates, so the only state here is which step is open.
//
// Server-rendered state is the source of truth — each step carries
// data-state="done|editing|locked|optional" and the server decides which one
// opens after a create. This controller only handles reopening a collapsed
// step and closing whichever was open, so a Turbo re-render can't disagree
// with it.
export default class extends Controller {
  static targets = ["step", "progressCount", "progressBar", "progressNote"]

  toggle(event) {
    const step = event.currentTarget.closest(
      "[data-season-wizard-target='step']",
    )
    if (!step || step.dataset.state === "locked") return

    const body = step.querySelector("[data-wizard-body]")
    if (!body) return

    const opening = body.classList.contains("hidden")
    if (opening) this._closeOthers(step)
    body.classList.toggle("hidden", !opening)
    step.dataset.open = String(opening)
    this._syncChevron(step, opening)
  }

  // Step 3 finishing. Nothing in it is required and its fields save
  // themselves, so this only collapses the step and marks how it ended.
  resolve(event) {
    const step = event.currentTarget.closest(
      "[data-season-wizard-target='step']",
    )
    if (!step) return

    step.dataset.state = event.currentTarget.dataset.outcome
    step.querySelector("[data-wizard-body]")?.classList.add("hidden")
    step.dataset.open = "false"
    this._syncChevron(step, false)
    this._paintResolved(step)
    this._syncProgress()
  }

  _paintResolved(step) {
    const badge = step.querySelector("[data-wizard-badge]")
    if (badge) {
      badge.classList.remove("bg-vivid-purple", "text-white")
      badge.classList.add("bg-green-500/15", "text-green-500")
      badge.innerHTML =
        '<svg viewBox="0 0 20 20" fill="currentColor" class="h-3.5 w-3.5" aria-hidden="true">' +
        '<path fill-rule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 8a1 1 0 0 1-1.5 0l-3.5-3.7a1 1 0 1 1 1.4-1.4l2.8 2.9 6.8-7.2a1 1 0 0 1 1.5 0Z" clip-rule="evenodd" /></svg>'
    }
    const status = step.querySelector("[data-wizard-status]")
    if (status) {
      status.textContent = step.dataset.state === "skipped" ? "Skipped" : "Done"
      status.classList.remove("text-vivid-purple")
      status.classList.add("text-app-text-faint")
    }
  }

  _syncProgress() {
    const resolved = this.stepTargets.filter((s) =>
      ["done", "skipped"].includes(s.dataset.state),
    ).length
    if (this.hasProgressCountTarget) {
      this.progressCountTarget.textContent = `${resolved} of ${this.stepTargets.length}`
    }
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${Math.round((resolved / this.stepTargets.length) * 100)}%`
    }
  }

  _closeOthers(keep) {
    this.stepTargets.forEach((step) => {
      if (step === keep || step.dataset.state === "locked") return
      step.querySelector("[data-wizard-body]")?.classList.add("hidden")
      step.dataset.open = "false"
      this._syncChevron(step, false)
    })
  }

  _syncChevron(step, open) {
    step
      .querySelector("[data-wizard-chevron]")
      ?.classList.toggle("rotate-180", open)
  }
}
